import React, { useState, useEffect, useRef } from 'react';
import './BoardPoker.css';
import CardComponent from '../../common/Cards';
import PlayerLabel from '../../common/PlayerLabel';
import RoomControls from '../../common/RoomControls';
import useAudio from '../../UseAudio';

const HAND_RANKINGS = [
  { name: 'Royal Flush', cards: [{rank:'A', suit:'S'}, {rank:'K', suit:'S'}, {rank:'Q', suit:'S'}, {rank:'J', suit:'S'}, {rank:'10', suit:'S'}] },
  { name: 'Straight Flush', cards: [{rank:'9', suit:'H'}, {rank:'8', suit:'H'}, {rank:'7', suit:'H'}, {rank:'6', suit:'H'}, {rank:'5', suit:'H'}] },
  { name: 'Four of a Kind', cards: [{rank:'A', suit:'S'}, {rank:'A', suit:'H'}, {rank:'A', suit:'D'}, {rank:'A', suit:'C'}, {rank:'K', suit:'S'}] },
  { name: 'Full House', cards: [{rank:'K', suit:'S'}, {rank:'K', suit:'H'}, {rank:'K', suit:'D'}, {rank:'Q', suit:'S'}, {rank:'Q', suit:'H'}] },
  { name: 'Flush', cards: [{rank:'A', suit:'D'}, {rank:'J', suit:'D'}, {rank:'8', suit:'D'}, {rank:'6', suit:'D'}, {rank:'2', suit:'D'}] },
  { name: 'Straight', cards: [{rank:'5', suit:'S'}, {rank:'4', suit:'H'}, {rank:'3', suit:'D'}, {rank:'2', suit:'C'}, {rank:'A', suit:'S'}] },
  { name: 'Three of a Kind', cards: [{rank:'Q', suit:'S'}, {rank:'Q', suit:'H'}, {rank:'Q', suit:'D'}, {rank:'J', suit:'S'}, {rank:'10', suit:'C'}] },
  { name: 'Two Pair', cards: [{rank:'J', suit:'S'}, {rank:'J', suit:'H'}, {rank:'10', suit:'D'}, {rank:'10', suit:'C'}, {rank:'9', suit:'S'}] },
  { name: 'Pair', cards: [{rank:'A', suit:'S'}, {rank:'A', suit:'H'}, {rank:'K', suit:'D'}, {rank:'Q', suit:'C'}, {rank:'J', suit:'S'}] },
  { name: 'High Card', cards: [{rank:'A', suit:'S'}, {rank:'K', suit:'H'}, {rank:'J', suit:'D'}, {rank:'8', suit:'C'}, {rank:'5', suit:'S'}] },
];

export default function BoardPoker({ state: gs, send, myPlayerId, onLeave }) {
  const { playSound } = useAudio();
  const [raiseAmount, setRaiseAmount] = useState(0);
  const prevPhase = useRef(null);
  const wasMyTurn = useRef(false);
  const prevCommCount = useRef(0);

  const myIndex = gs.your_index;
  const players = gs.players || [];
  const myPlayer = players[myIndex] || {};
  const isMyTurn = gs.current_turn === myIndex && gs.phase === 'playing';

  useEffect(() => {
    if (gs.phase === 'playing' && prevPhase.current !== 'playing') {
      if (prevPhase.current === null) {
        playSound('startGame');
      }
      playSound('cardShuffle');
    }
    prevPhase.current = gs.phase;
  }, [gs.phase, playSound]);

  useEffect(() => {
    const isMyTurnNow = gs.phase === 'playing' && gs.current_turn === myIndex;
    if (isMyTurnNow && !wasMyTurn.current) {
      playSound('yourTurn');
    }
    wasMyTurn.current = isMyTurnNow;
  }, [gs.phase, gs.current_turn, myIndex, playSound]);

  useEffect(() => {
    const commCount = gs.community_cards?.length || 0;
    if (commCount > prevCommCount.current) {
      playSound('cardPlay');
    }
    prevCommCount.current = commCount;
  }, [gs.community_cards, playSound]);

  useEffect(() => {
    if (isMyTurn) {
      const currentBet = gs.current_bet || 0;
      const minRaise = gs.min_raise || 20;
      const min = currentBet + minRaise;
      const max = (myPlayer.chips || 0) + (myPlayer.in_pot || 0);
      setRaiseAmount(Math.min(min, max));
    }
  }, [isMyTurn, gs.current_bet, gs.min_raise, myPlayer.in_pot, myPlayer.chips]);

  const handleAction = (type, payload = {}) => {
    if (!isMyTurn) return;
    send(type, payload);
  };

  const getRelativeIndex = (idx) => {
    if (myIndex === -1) return idx;
    return (idx - myIndex + players.length) % players.length;
  };

  const communityCards = gs.community_cards || [];
  const winners = gs.winners || [];

  const col1 = HAND_RANKINGS.slice(0, 5);
  const col2 = HAND_RANKINGS.slice(5, 10);

  const currentBet = gs.current_bet || 0;
  const myInPot = myPlayer.in_pot || 0;
  const callAmount = currentBet - myInPot;
  const minRaiseAmount = gs.min_raise || 20;
  const minRaiseTo = currentBet + minRaiseAmount;
  const maxRaiseTo = (myPlayer.chips || 0) + myInPot;

  return (
    <div className="poker-board" data-players={players.length}>
      <RoomControls roomId={gs.room_id} onLeave={onLeave} />
      
      <div className="rankings-trigger-static">
        <button className="rankings-btn">Hand Rankings ⓘ</button>
      </div>

      <div className="rankings-overlay-fullscreen">
        <div className="rankings-content">
          <div className="rankings-grid-2col">
            <div className="ranking-column">
              {col1.map((rank, i) => (
                <div key={i} className="ranking-card-row">
                  <span className="rank-name">{rank.name}</span>
                  <div className="ranking-example-cards">
                    {rank.cards.map((c, ci) => (
                      <div key={ci} className="mini-svg-card">
                        <CardComponent card={c} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="ranking-column">
              {col2.map((rank, i) => (
                <div key={i+5} className="ranking-card-row">
                  <span className="rank-name">{rank.name}</span>
                  <div className="ranking-example-cards">
                    {rank.cards.map((c, ci) => (
                      <div key={ci} className="mini-svg-card">
                        <CardComponent card={c} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {gs.phase === 'match_over' && (
        <div className="showdown-status-bar">Showdown</div>
      )}

      <div className="poker-table">
        <div className="table-surface">
          <div className="pot-container">
             <span className="pot-label">Total Pot</span>
             <span className="pot-amount">${gs.pot}</span>
          </div>
          <div className="community-cards">
            {communityCards.map((card, i) => (
              <div key={i} style={{ width: '85px' }}>
                <CardComponent card={card} />
              </div>
            ))}
            {[...Array(5 - communityCards.length)].map((_, i) => (
              <div key={`empty-${i}`} style={{ width: '85px', opacity: 0.1 }}>
                <CardComponent faceDown />
              </div>
            ))}
          </div>
        </div>

        {players.map((p, i) => {
          if (!p) return null;
          const relIdx = getRelativeIndex(i);
          const winIdx = winners.indexOf(i);
          const isWinner = winIdx !== -1;
          const isOpposite = (players.length === 6 && relIdx === 3) || (players.length === 4 && relIdx === 2);
          
          const infoRow = (
            <div className="player-info-horizontal">
              <PlayerLabel 
                player={p} 
                playerId={i}
                gs={gs}
                myId={myIndex}
                send={send}
              />
              <div className="player-chips">${p.chips ?? 0}</div>
            </div>
          );

          const actionRow = (
            <div className="player-action-row">
              <div className="player-cards">
                {i === myIndex ? (
                  gs.your_hand?.map((c, ci) => (
                    <div key={ci} style={{ width: '80px', marginLeft: ci > 0 ? '-35px' : 0, zIndex: ci }}>
                      <CardComponent card={c} />
                    </div>
                  ))
                ) : (
                  !p.is_folded && [0, 1].map(ci => (
                    <div key={ci} style={{ width: '60px', marginLeft: ci > 0 ? '-30px' : 0, zIndex: ci }}>
                      <CardComponent 
                        faceDown={gs.phase !== 'match_over'} 
                        card={gs.phase === 'match_over' ? p.hand?.[ci] : null} 
                      />
                    </div>
                  ))
                )}
              </div>
              {p.in_pot > 0 && <div className="player-bet">${p.in_pot}</div>}
            </div>
          );

          return (
            <div key={i} className={`player-position pos-${relIdx} ${isWinner ? 'winner-spotlight' : ''}`}>
               {isOpposite ? (
                 <>
                   {infoRow}
                   {actionRow}
                   {isWinner && <div className="winner-label-overlay">{gs.winner_hands[winIdx]}</div>}
                   {p.is_folded && <div className="folded-label">FOLDED</div>}
                   {p.is_all_in && <div className="all-in-label">ALL-IN</div>}
                 </>
               ) : (
                 <>
                   {isWinner && <div className="winner-label-overlay">{gs.winner_hands[winIdx]}</div>}
                   {p.is_folded && <div className="folded-label">FOLDED</div>}
                   {p.is_all_in && <div className="all-in-label">ALL-IN</div>}
                   {actionRow}
                   {infoRow}
                 </>
               )}
            </div>
          );
        })}
      </div>

      <div className="poker-controls">
        {gs.phase === 'match_over' ? (
          <button className="btn-poker-gold" onClick={() => send('next_round')}>
            START NEXT ROUND
          </button>
        ) : isMyTurn ? (
          <>
            <button className="action-btn btn-fold" onClick={() => handleAction('fold')}>
              FOLD
              <span>Surrender</span>
            </button>
            
            <button 
              className="action-btn btn-check" 
              onClick={() => handleAction('check')}
              disabled={callAmount > 0}
              style={{ opacity: callAmount > 0 ? 0.5 : 1, cursor: callAmount > 0 ? 'not-allowed' : 'pointer' }}
            >
              CHECK
              <span>Pass</span>
            </button>

            <button 
              className="action-btn btn-call" 
              onClick={() => handleAction('call')}
              disabled={callAmount <= 0}
              style={{ opacity: callAmount <= 0 ? 0.5 : 1, cursor: callAmount <= 0 ? 'not-allowed' : 'pointer' }}
            >
              CALL
              <span>${callAmount > 0 ? callAmount : 0}</span>
            </button>
            
            {myPlayer.chips > 0 && maxRaiseTo >= minRaiseTo && (
              <div className="raise-slider-group">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '5px', color: '#94a3b8' }}>
                    RAISE TO: ${raiseAmount}
                  </span>
                  <input 
                    type="range" 
                    min={minRaiseTo} 
                    max={maxRaiseTo} 
                    value={raiseAmount} 
                    onChange={(e) => setRaiseAmount(parseInt(e.target.value))}
                  />
                </div>
                <button className="action-btn btn-raise" onClick={() => handleAction('raise', { amount: raiseAmount })}>
                  RAISE
                </button>
              </div>
            )}
            
            <button className="action-btn btn-allin" onClick={() => handleAction('all_in')}>
              ALL IN
              <span>Bet ${myPlayer.chips}</span>
            </button>
          </>
        ) : (
          <div style={{ color: '#475569', fontSize: '0.8rem', letterSpacing: '4px', fontWeight: 'bold' }}>
            {gs.phase === 'playing' ? `WAITING FOR ${players[gs.current_turn]?.name?.toUpperCase()}` : 'ROUND OVER'}
          </div>
        )}
      </div>
    </div>
  );
}
